import api from './api';
import { ChatListItem, ChatMessage, ChatMessageImage, ChatMilestone, ChatWithMessages } from '../models/chat';

export interface ChatImageUploadResult {
  imageId: string;
  thumbnailUrl: string;
  width: number;
  height: number;
}

export async function listProjectChats(projectId: string): Promise<ChatListItem[]> {
  const { data } = await api.get<ChatListItem[]>(`project/${projectId}/chats`);
  return data;
}

/** The chat the project page opens on: the most recently updated one, or null when the project has none. */
export function latestChat(chats: ChatListItem[]): ChatListItem | null {
  return chats.reduce<ChatListItem | null>(
    (latest, chat) => (!latest || Date.parse(chat.updatedAt) > Date.parse(latest.updatedAt) ? chat : latest),
    null,
  );
}

export async function createChat(
  projectId: string,
  firstMessage: string,
  name?: string,
  imageIds?: string[],
): Promise<ChatWithMessages> {
  const { data } = await api.post<ChatWithMessages>(`project/${projectId}/chats`, {
    name,
    firstMessage,
    imageIds,
  });
  return data;
}

export async function getChatMessages(chatId: string): Promise<ChatMessage[]> {
  const { data } = await api.get<ChatMessage[]>(`chats/${chatId}/messages`);
  return data;
}

export async function getChatMilestones(chatId: string): Promise<ChatMilestone[]> {
  const { data } = await api.get<ChatMilestone[]>(`chats/${chatId}/milestones`);
  return data;
}

export async function sendChatMessage(
  chatId: string,
  content: string,
  imageIds?: string[],
): Promise<{ jobId: string }> {
  const { data } = await api.post<{ jobId: string }>(`chats/${chatId}/messages`, { content, imageIds });
  return data;
}

/**
 * Sends the creator's words as the project page's composer does, on the chat that page opens on, or on a new chat
 * when the project has none. Resolves to the job the message started.
 */
export async function sendToProjectChat(projectId: string, content: string): Promise<string> {
  const trimmed = content.trim();
  const latest = latestChat(await listProjectChats(projectId));
  if (latest) return (await sendChatMessage(latest.id, trimmed)).jobId;

  const { chat } = await createChat(projectId, trimmed);
  if (!chat.currentJobId) throw new Error('The new chat started no job.');
  return chat.currentJobId;
}

export async function uploadChatImage(
  projectId: string,
  file: File,
  chatId?: string,
): Promise<ChatImageUploadResult> {
  const formData = new FormData();
  formData.append('file', file);
  if (chatId) {
    formData.append('chatId', chatId);
  }
  const { data } = await api.post<ChatImageUploadResult>(`project/${projectId}/chat-images`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function deleteChatImage(projectId: string, imageId: string): Promise<void> {
  await api.delete(`project/${projectId}/chat-images/${imageId}`);
}

export function chatImageUploadResultToMessageImage(result: ChatImageUploadResult): ChatMessageImage {
  return {
    id: result.imageId,
    thumbnailUrl: result.thumbnailUrl,
    width: result.width,
    height: result.height,
  };
}
